import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { AlertTriangle, Cable, Loader2, Save } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useDmpWriteConfirm } from '@/hooks/useDmpWriteConfirm';
import { updateAddressIdentity, type EditableAddressBody } from '@/hooks/useAddressValidation';
import { PostalCodeLocalityHint } from '@/components/tickets/PostalCodeLocalityHint';
import type { DmpLookupResponse } from '@uniticket/shared';

/**
 * Correct a DMP address's identity from the console (user ask 2026-08-28). Admins only — the page hands
 * this form the looked-up address, and the server gates the route on SYSTEM_ADMIN regardless.
 *
 * Why it exists: side requests arrive that are not tickets ("the box on this jvid is wrong"), and the
 * only place a bus number could be edited was the post-release panel of a ticket's apartment sheet —
 * which needs a ticket AND a released address, so on an unreleased building it simply was not there.
 *
 * Scope is the address's IDENTITY only. Its deployment and visibility (status, delivery status, the
 * scope lists, release) each keep their own action with their own guards; a free-form edit form must
 * not become a way around them.
 */

const FIELDS = [
  { key: 'streetName', labelKey: 'fields.street', required: true },
  { key: 'houseNumber', labelKey: 'fields.houseNumber', required: true },
  { key: 'houseNumberExtension', labelKey: 'fields.houseNumberExt', required: false },
  { key: 'busNumber', labelKey: 'fields.busNumber', required: false },
  { key: 'postalCode', labelKey: 'fields.postalCode', required: true },
  { key: 'municipality', labelKey: 'fields.municipality', required: true },
  { key: 'city', labelKey: 'fields.city', required: true },
] as const;

type Key = typeof FIELDS[number]['key'];

export function EditAddressForm({
  address,
  onSaved,
  onAttachFakeOdf,
}: {
  address: DmpLookupResponse;
  onSaved: (jvid: number) => void;
  /** Ouvre l'aperçu « poser un fake ODF » (rendu par la page, hors du Sheet). Absent ⇒ pas de bouton. */
  onAttachFakeOdf?: () => void;
}) {
  const { t } = useTranslation(['tickets']);
  const confirmDmpWrite = useDmpWriteConfirm();
  const ref = address.reference;

  // The loaded row IS the baseline: the Save button stays disabled until something actually moves, and
  // only the moved fields are sent — a form that posts all seven would make every save look like a
  // seven-field rewrite in the journal.
  const initial = Object.fromEntries(
    FIELDS.map((f) => [f.key, (ref as unknown as Record<string, string | null>)[f.key] ?? '']),
  ) as Record<Key, string>;

  const [form, setForm] = useState<Record<Key, string>>(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const changed = FIELDS.filter((f) => form[f.key].trim() !== (initial[f.key] ?? '').trim());
  const missing = FIELDS.filter((f) => f.required && form[f.key].trim() === '');
  const connected = address.attributes?.status === 'HOMES_CONNECT';
  // Proximus a blacklisté ce JVID ⇒ la correction ne va PAS dans public.address mais dans la déclaration
  // de l'opérateur (décision 01-09). Le dire AVANT la saisie, pas après : l'écran est le seul endroit où
  // l'on peut encore renoncer, et « j'ai corrigé l'adresse » ne veut pas dire la même chose des deux côtés.
  const blacklisted = (address.buildFiles ?? []).some((f) => f.kind === 'BLACKLIST' && f.found);

  async function save() {
    if (changed.length === 0 || missing.length > 0) return;
    const confirmKey = blacklisted
      ? 'tickets:dmpLookup.edit.confirmActionMapping'
      : 'tickets:dmpLookup.edit.confirmAction';
    if (!confirmDmpWrite(t(confirmKey, { jvid: ref.jvid }))) return;
    setSaving(true);
    setError(null);
    try {
      const body = Object.fromEntries(changed.map((f) => [f.key, form[f.key].trim()])) as EditableAddressBody;
      const res = await updateAddressIdentity(ref.jvid, body);
      if (res.data.updated === 0) toast.warning(t('tickets:dmpLookup.edit.noop'));
      // Le serveur dit où il a écrit ; on ne le déduit pas côté écran, sinon les deux peuvent diverger.
      else if (res.data.target === 'mapping') toast.success(t('tickets:dmpLookup.edit.doneMapping', { count: res.data.changed.length }));
      else toast.success(t('tickets:dmpLookup.edit.done', { count: res.data.changed.length }));
      onSaved(ref.jvid);
    } catch (err) {
      // 409 = another live address already carries these details; the server names its jvid. Surfaced
      // verbatim: "which one" is the whole answer the admin needs.
      setError((err as Error)?.message || t('tickets:dmpLookup.edit.error'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <h3 className="text-sm font-bold text-slate-800 mb-1">{t('tickets:dmpLookup.edit.title')}</h3>
      <p className="text-[11px] text-slate-400 mb-3">
        {t('tickets:dmpLookup.edit.subtitle', { jvid: ref.jvid })}
      </p>

      {blacklisted && (
        <div className="flex items-start gap-2 mb-3 rounded-md border border-rose-300 bg-rose-50 px-3 py-2" role="note">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-rose-600" />
          <span className="text-xs text-rose-800">{t('tickets:dmpLookup.edit.blacklistedWarning')}</span>
        </div>
      )}

      {connected && (
        <div className="flex items-start gap-2 mb-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2" role="note">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-amber-600" />
          <span className="text-xs text-amber-800">{t('tickets:dmpLookup.edit.connectedWarning')}</span>
        </div>
      )}

      {/* Poser un fake ODF (demande user 2026-09-02). Une ACTION à part, pas un champ du formulaire :
          l'enregistrement ne poste que les champs d'identité qui ont bougé, et reste désactivé quand
          rien n'a bougé — or le cas qui a motivé la demande est exactement celui-là, une adresse juste
          à qui il manque la fibre. Une case à cocher ici n'aurait donc jamais pu être soumise.
          Affiché seulement quand l'adresse N'A PAS de fibre : proposer de poser un placeholder sur une
          adresse déjà fibrée n'aurait aucun sens, et le serveur le refuserait. */}
      {onAttachFakeOdf && address.attributes?.hasFiber === false && (
        <div className="mb-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
          <p className="flex items-center gap-1.5 text-xs font-medium text-slate-700">
            <Cable className="h-3.5 w-3.5 text-slate-500" />
            {t('tickets:dmpLookup.edit.noFiber')}
          </p>
          <p className="mt-0.5 text-[11px] text-slate-500">{t('tickets:dmpLookup.edit.noFiberHint')}</p>
          <Button
            size="sm"
            variant="outline"
            className="mt-2 h-7 px-2 text-[11px]"
            disabled={saving || changed.length > 0}
            onClick={onAttachFakeOdf}
          >
            <Cable className="mr-1 h-3 w-3" />
            {t('tickets:dmpLookup.edit.attachFakeOdf')}
          </Button>
          {/* L'aperçu remplace ce panneau à l'écran : le dire avant, plutôt que de laisser perdre
              une saisie en cours. */}
          {changed.length > 0 && (
            <p className="mt-1 text-[11px] text-slate-400">{t('tickets:dmpLookup.edit.attachFakeOdfBusy')}</p>
          )}
        </div>
      )}

      <div className="space-y-2.5">
        {FIELDS.map((f) => {
          const moved = form[f.key].trim() !== (initial[f.key] ?? '').trim();
          const blank = f.required && form[f.key].trim() === '';
          return (
            <div key={f.key} className="grid grid-cols-[110px_1fr] items-center gap-2">
              <label className="text-xs text-slate-500" htmlFor={`edit-${f.key}`}>
                {t(`tickets:validation.${f.labelKey}`)}
                {f.required && <span className="text-rose-600 ml-0.5">*</span>}
              </label>
              <Input
                id={`edit-${f.key}`}
                value={form[f.key]}
                onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                disabled={saving}
                // The browser's own address autofill has no business here: this form edits the DMP's
                // addresses, not the admin's, and one stray click would paste a home address into a
                // production row.
                //
                // autoComplete="off" was the first attempt and it does NOT work (user report
                // 2026-08-28, still suggesting on postal code 5310): Chrome deliberately overrides
                // "off" on fields its classifier reads as an address, and every signal here says
                // address — the id, the label, the neighbours. "new-password" works where "off" fails
                // because it is not a request to disable anything, it is a DECLARED TYPE: the classifier
                // takes the declaration and stops guessing. The field stays type="text", so no password
                // UI appears.
                //
                // The name is deliberately opaque for the same reason — "postalCode" is one of the
                // strings the heuristic looks for. Nothing submits this form natively, so the name only
                // ever serves the browser.
                autoComplete="new-password"
                name={`dmp-f-${FIELDS.findIndex((x) => x.key === f.key)}`}
                data-1p-ignore
                data-lpignore="true"
                className={
                  blank ? 'h-8 text-sm bg-rose-50 border-rose-300'
                    : moved ? 'h-8 text-sm bg-amber-50 border-amber-300'
                      : 'h-8 text-sm'
                }
              />
            </div>
          );
        })}
      </div>

      {/* Same postal-code check as the validation window and the apartment sheet (user ask 2026-08-28):
          correcting a postal code here is the same act, so it gets the same safety net. Active only once
          the code differs from the loaded value. */}
      <div className="mt-3">
        <PostalCodeLocalityHint
          postalCode={form.postalCode}
          city={form.city}
          active={form.postalCode.trim() !== (initial.postalCode ?? '').trim()}
          disabled={saving}
          onPick={(c, m) => setForm((prev) => ({ ...prev, city: c, municipality: m }))}
        />
      </div>

      <p className="mt-3 text-[11px] text-slate-400">{t('tickets:dmpLookup.edit.hint')}</p>

      {missing.length > 0 && (
        <p className="mt-2 text-[11px] font-medium text-rose-600" role="alert">
          {t('tickets:dmpLookup.edit.requiredBlank')}
        </p>
      )}
      {error && <p className="mt-2 text-[11px] font-medium text-rose-600" role="alert">{error}</p>}

      <Button
        onClick={save}
        disabled={saving || changed.length === 0 || missing.length > 0}
        className="mt-4 w-full bg-brand-gradient text-white hover:opacity-90"
      >
        {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
        {changed.length === 0
          ? t('tickets:dmpLookup.edit.noChange')
          : t('tickets:dmpLookup.edit.save', { count: changed.length })}
      </Button>
    </div>
  );
}
